import type { ComponentType, LazyExoticComponent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { ConfigEntry } from '../../services/config.service';

/**
 * Frontend side of the module contract
 *
 * Each module in `src/modules/<id>/module.tsx` exports one manifest. The core
 * builds routes, providers, widgets and navigation from the manifests of the
 * modules active in this instance (GET /api/instance). The id must equal the
 * backend module id.
 */

type PageComponent = ComponentType | LazyExoticComponent<ComponentType>;

export interface ModuleRoute {
  /** Path below `/`, e.g. `wallbox/charges` or `retreat/billing/:eventId`. */
  path: string;
  /** Menu key the user needs; omitted = any signed-in user. */
  menuKey?: string;
  element: PageComponent;
}

export interface PublicRoute {
  /** Absolute path, reachable without sign-in. */
  path: string;
  element: PageComponent;
}

/** A link inside a module's navigation block. */
export interface NavItem {
  menuKey: string;
  path: string;
  label: string;
  icon: LucideIcon;
  /** Small heading rendered above the first item of a section. */
  section?: string;
}

/**
 * What the core needs to render one menu entry (a row in menu_permissions).
 * Label, icon, position and visibility of the entry itself come from the
 * database; the node adds the page path or the module's inner links.
 */
export interface NavNode {
  menuKey: string;
  /** Page of the entry itself (e.g. the dashboard or a single-page module). */
  path?: string;
  /** Inner links of a module (e.g. all pages of the retreat module). */
  items?: NavItem[];
  /** Rendered above the items, e.g. an event selector. */
  header?: ComponentType<{ onSelect?: () => void }>;
  /** Icon when the database has none. */
  defaultIcon?: LucideIcon;
}

/** Props of a module's own tab in Administration › Settings. */
export interface SettingsPanelProps {
  /** Stored values of the tab (incl. keys matched by the category's keyPrefix). */
  entries: ConfigEntry[];
  save: (key: string, value: string) => void;
  /** The core's generic field for a value (label, input type, placeholders). */
  renderField: (entry: ConfigEntry) => ReactNode;
}

export interface FrontendModule {
  id: string;
  /** Display name, e.g. for widget categories. */
  name: string;
  routes?: ModuleRoute[];
  publicRoutes?: PublicRoute[];
  nav?: NavNode[];
  /** Context providers wrapped around the signed-in layout while the module is active. */
  providers?: ComponentType<{ children: ReactNode }>[];
  /** Registers the module's dashboard widgets. */
  registerWidgets?: () => void;
  /**
   * Where a visitor who is not signed in lands on `/` (e.g. the last public
   * program they opened). The first module returning a path wins.
   */
  publicLanding?: () => string | null;
  /**
   * Own rendering of settings tabs, by category id (see the backend's
   * settingCategories). Other tabs use the generic fields.
   */
  settingsPanels?: Record<string, ComponentType<SettingsPanelProps> | LazyExoticComponent<ComponentType<SettingsPanelProps>>>;
}

export function defineFrontendModule(mod: FrontendModule): FrontendModule {
  return mod;
}
